import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { PeerReviewService } from '../services/peer-review/peer-review.service';
import { RegisterPeerReviewerRequest } from '../models/super-admin/registerPeerReviewerRequest';
import { UpdatePeerReviewerRequest } from '../models/super-admin/updatePeerReviewerRequest';
import { PeerReviewerState } from '../models/enums/peerReviewerState';
import { Gender } from '../models/enums/enums';
import { SpeakingCareerAreasEnum } from '../models/enums/speakingCareerAreasEnum';

@Component({
  selector: 'app-peer-signup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    RadioButtonModule,
    MultiSelectModule,
    InputTextareaModule,
    CheckboxModule,
    ToastModule,
    AccordionModule
  ],
  providers: [MessageService],
  templateUrl: './peer-signup.component.html',
  styleUrls: ['./peer-signup.component.scss']
})
export class PeerSignupComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  showIntro = true;
  currentStep = 1;
  stepTitles = ['About you', 'Education', 'Test Scores', 'Experience', 'Review'];
  stepSubtitles = ['Personal & contact info', 'Degrees & institutions', 'GRE, GMAT, TOEFL...', 'Work & research history', 'Confirm & submit'];

  faqs = [
    { question: 'Who can sign up as a GradPilots peer?', answer: 'Anyone who has studied or is currently studying at a university. Undergraduate, postgraduate, or recent graduate, if you have genuine experience to share, we want you on the platform.' },
    { question: 'How much time does being a peer involve?', answer: 'That is entirely up to you. There is no set commitment or minimum number of calls. You decide how available you want to be and students book around your schedule.' },
    { question: 'Will my personal contact details be shared with students?', answer: 'No. Your email address and phone number are never shown publicly. Students connect with you through the GradPilots platform, not directly.' },
    { question: 'Can I update or remove my profile after signing up?', answer: 'Yes, at any time. Just email the GradPilots team and your profile will be updated or removed within seven days.' },
    { question: 'What kinds of questions do students ask?', answer: 'Because students are matched to peers based on university, subject, and background, they come with specific questions rather than generic ones. They are asking about your course, your entry journey, what life at your particular university is actually like, things no brochure can answer.' },
    { question: 'Do I need to have graduated to take part?', answer: 'No. Current students are just as valuable as graduates. If you are in your second year or beyond, you already have more than enough to offer someone who is just applying.' },
    { question: 'What happens after I submit the form?', answer: 'The GradPilots team reviews your profile within two to three working days. Once approved, your profile goes live and students can start requesting calls.' }
  ];

  signupForm!: FormGroup;
  submitted = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  isEditMode = false;
  editPeerId = '';
  selectedProfilePhotoName = '';
  selectedProfilePhotoFile: File | null = null;

  studyLevels = [
    { label: "Undergraduate / Bachelor's", value: "Undergraduate" },
    { label: "Postgraduate / Master's / MPhil", value: "Postgraduate" }
  ];

  genderOptions = [
    { label: 'Male', value: Gender.Male },
    { label: 'Female', value: Gender.Female },
    { label: 'Other', value: Gender.Other }
  ];

  careerAreasOptions = [
    { label: 'Finance and Consulting', value: SpeakingCareerAreasEnum.FinanceAndConsulting },
    { label: 'Technology and Computer Science', value: SpeakingCareerAreasEnum.TechnologyComputerScience },
    { label: 'Law and Policy', value: SpeakingCareerAreasEnum.LawAndPolicy },
    { label: 'Engineering and Sciences', value: SpeakingCareerAreasEnum.EngineeringSciences },
    { label: 'Medicine, Healthcare and Research', value: SpeakingCareerAreasEnum.MedicineHealthcareResearch },
    { label: 'Business and Entrepreneurship', value: SpeakingCareerAreasEnum.BusinessEntrepreneurship }
  ];


  static readonly Validators = {
    ALPHABET: /^[A-Za-z\s'\-\.]{2,150}$/,
    NUMERIC: /^\d+(\.\d+)?$/,
    ALPHANUMERIC: /^[a-zA-Z0-9\s,.-@/#()!%]*$/,
    LINKEDIN: /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|pub|company)\/[A-Za-z0-9\-_%]+\/?$/i,
    PHONE: /^[0-9]{7,15}$/,
    YEAR: /^\d{4}$/,
    SAT_OVERALL: /^([4-9][0-9]{2}|1[0-4][0-9]{2}|1[5][0-9]{2}|1600)$/,
    SAT_SECTION: /^([2-7][0-9]{2}|800)$/,
    ACT_OVERALL: /^([1-9]|[12][0-9]|3[0-6])$/,
    ACT_SECTION: /^([1-9]|[12][0-9]|3[0-6])$/,
    IELTS: /^([0-8](\.[05])?|9(\.0?)?)$/,
    TOEFL: /^([0-9]|[1-9][0-9]|1[01][0-9]|120)$/,
    GRE_TOTAL: /^(2[6-9][0-9]|3[0-3][0-9]|340)$/,
    GRE_SECTION: /^(1[3-6][0-9]|170)$/,
    GMAT: /^([2-7][0-9]{2}|800)$/,
  } as const;

  constructor(
    private fb: FormBuilder,
    private peerReviewService: PeerReviewService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initializeForm();

    this.signupForm.get('StudyLevel')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateUniversityValidators());

    if (this.signupForm.get('StudyLevel')?.value !== 'Postgraduate') {
      this.signupForm.patchValue({
        SecondUniversityName: '',
        SecondUniversityCountry: '',
        SecondUniversityCity: '',
        SecondGraduationYear: '',
        SecondDegreeSubject: '',
        SecondGradeAchieved: '',
      });
    }

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params['edit'] === 'true' && params['id']) {
          this.isEditMode = true;
          this.editPeerId = params['id'];
          this.showIntro = false;
          this.loadPeerData(params['id']);
        }
      });
  }

  private updateUniversityValidators(): void {
    const isPostgrad = this.isPostgraduate;
    const controls = [
      { name: 'SecondUniversityName', required: isPostgrad },
      { name: 'SecondUniversityCountry', required: isPostgrad },
      { name: 'SecondUniversityCity', required: isPostgrad },
      { name: 'SecondGraduationYear', required: isPostgrad },
      { name: 'SecondDegreeSubject', required: isPostgrad }
    ];

    controls.forEach(ctrl => {
      const formControl = this.signupForm.get(ctrl.name);
      if (!formControl) return;
      if (ctrl.required) {
        let validators = [Validators.required];
        if (ctrl.name.includes('Year')) {
          validators.push(Validators.pattern(PeerSignupComponent.Validators.NUMERIC));
        } else if (ctrl.name.includes('Country') || ctrl.name.includes('City')) {
          validators.push(Validators.pattern(PeerSignupComponent.Validators.ALPHABET));
          validators.push(Validators.minLength(2));
          validators.push(Validators.maxLength(60));
        } else {
          validators.push(Validators.pattern(PeerSignupComponent.Validators.ALPHANUMERIC));
          validators.push(Validators.minLength(2));
          validators.push(Validators.maxLength(150));
        }
        formControl.setValidators(validators);
      } else {
        formControl.clearValidators();
      }
      formControl.updateValueAndValidity();
    });
  }

  get isPostgraduate(): boolean {
    return this.signupForm.get('StudyLevel')?.value === 'Postgraduate';
  }

  get isUndergraduate(): boolean {
    return this.signupForm.get('StudyLevel')?.value === 'Undergraduate';
  }

  private initializeForm(): void {
    const V = PeerSignupComponent.Validators;

    this.signupForm = this.fb.group({

      // ─── STEP 1: ABOUT YOU ─────────────────────────────────────────────
      FirstName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(V.ALPHABET)
      ]],
      LastName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(V.ALPHABET)
      ]],
      Email: ['', [
        Validators.required,
        Validators.email,
        Validators.maxLength(100)
      ]],
      WhatsAppNumber: ['', [
        Validators.pattern(V.PHONE)
      ]],
      LinkedInUrl: ['', [
        Validators.pattern(V.LINKEDIN)
      ]],
      CurrentCity: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      CurrentCountry: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      OriginCity: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      OriginCountry: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      Gender: ['', Validators.required],
      ProfilePhoto: [null],

      // ─── STEP 2: EDUCATION ─────────────────────────────────────────────
      StudyLevel: ['', Validators.required],
      HighestLevelEducation: [''],

      // School
      SchoolName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(120),
        Validators.pattern(V.ALPHANUMERIC)
      ]],
      SchoolBoard: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHANUMERIC)
      ]],
      HighSchoolCompletionYear: ['', [
        Validators.pattern(V.NUMERIC)
      ]],
      ClassXResult: [''],
      ClassXIIResult: [''],
      CourseWork: ['', [
        Validators.maxLength(300)
      ]],

      // Undergraduate
      UniversityName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(150),
        Validators.pattern(V.ALPHANUMERIC)
      ]],
      UniversityCountry: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      UniversityCity: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      GraduationYear: ['', [
        Validators.required,
        Validators.pattern(V.YEAR)
      ]],
      DegreeSubject: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.pattern(V.ALPHANUMERIC)
      ]],
      GradeAchieved: [''],
      UndergradGpaDegreeClass: ['', [
        Validators.maxLength(30)
      ]],

      // Postgraduate (validators set dynamically via updateUniversityValidators)
      SecondUniversityName: ['', [
        Validators.minLength(2),
        Validators.maxLength(150),
        Validators.pattern(V.ALPHANUMERIC)
      ]],
      SecondUniversityCountry: ['', [
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      SecondUniversityCity: ['', [
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(V.ALPHABET)
      ]],
      SecondGraduationYear: ['', [
        Validators.pattern(V.YEAR)
      ]],
      SecondDegreeSubject: ['', [
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.pattern(V.ALPHANUMERIC)
      ]],
      SecondGradeAchieved: [''],

      // ─── STEP 3: TEST SCORES (all optional; validated only when filled) ──
      SATOverall: ['', [Validators.pattern(V.NUMERIC)]],
      SATEnglish: ['', [Validators.pattern(V.NUMERIC)]],
      SATMath: ['', [Validators.pattern(V.NUMERIC)]],

      ACTOverall: ['', [Validators.pattern(V.NUMERIC)]],
      ACTEnglish: ['', [Validators.pattern(V.NUMERIC)]],
      ACTMath: ['', [Validators.pattern(V.NUMERIC)]],

      IELTS: ['', [Validators.pattern(V.NUMERIC)]],
      TOEFL: ['', [Validators.pattern(V.NUMERIC)]],
      APScores: ['', [Validators.maxLength(200)]],

      GREOverall: ['', [Validators.pattern(V.NUMERIC)]],
      GREVerbal: ['', [Validators.pattern(V.NUMERIC)]],
      GREQuant: ['', [Validators.pattern(V.NUMERIC)]],

      GMATOverall: ['', [Validators.pattern(V.NUMERIC)]],
      GMATSubScores: ['', [Validators.maxLength(100)]],
      WorkExpBeforeMBA: ['', [Validators.maxLength(50)]],

      GraduateIELTS: ['', [Validators.pattern(V.NUMERIC)]],
      GraduateTOEFL: ['', [Validators.pattern(V.NUMERIC)]],

      // ─── STEP 4: EXPERIENCE ────────────────────────────────────────────
      ShortBio: ['', [
        Validators.required,
        Validators.minLength(20),
        Validators.maxLength(1000)
      ]],
      CareerAreas: this.fb.array(
        this.careerAreasOptions.map(() => this.fb.control(false))
      ),
      WorkHistory: this.fb.array([
        this.createWorkHistoryGroup(),
        this.createWorkHistoryGroup()
      ]),
      ScholarshipsAwards: ['', [Validators.maxLength(500)]],
      Extracurriculars: ['', [Validators.maxLength(500)]],

      // ─── STEP 5: REVIEW & SUBMIT ────────────────────────────────────────
      AgreedToTerms: [false, Validators.requiredTrue]
    });
  }

  private createWorkHistoryGroup(): FormGroup {
    return this.fb.group({
      company: ['', [Validators.maxLength(100)]],
      role: ['', [Validators.maxLength(100)]]
    });
  }

  get f(): any {
    return this.signupForm.controls;
  }

  get careerAreasArray(): FormArray {
    return this.signupForm.get('CareerAreas') as FormArray;
  }

  get selectedCareerAreas(): SpeakingCareerAreasEnum[] {
    return this.careerAreasOptions
      .filter((_, i) => this.careerAreasArray.at(i).value === true)
      .map(opt => opt.value);
  }

  get workHistory(): FormArray {
    return this.signupForm.get('WorkHistory') as FormArray;
  }

  addWorkHistory(): void {
    this.workHistory.push(this.createWorkHistoryGroup());
  }

  removeWorkHistory(index: number): void {
    if (this.workHistory.length > 1) {
      this.workHistory.removeAt(index);
    }
  }

  startSignup(): void {
    this.showIntro = false;
    this.currentStep = 1;
  }

  private loadPeerData(peerId: string): void {
    this.peerReviewService.getPeerReviewerById(peerId).subscribe({
      next: (response: any) => {
        const peer = response.result || response;
        this.populateFormWithPeerData(peer);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load peer data' });
      }
    });
  }

  private populateFormWithPeerData(peer: any): void {
    const postgradLabel = this.studyLevels.find(s => s.value === 'Postgraduate')?.label;
    const hasPostgradData = !!(peer.graduateCollageName || peer.graduateMajorOrDegree || peer.graduateOrExpectedGraduationCompletionYear);

    const studyLevel = (peer.highestLevelEducation === postgradLabel || hasPostgradData)
      ? 'Postgraduate'
      : 'Undergraduate';

    // PATCH STEP 1: ABOUT YOU
    this.signupForm.patchValue({
      FirstName: peer.firstName ? peer.firstName.charAt(0).toUpperCase() + '.' : '',
      LastName: peer.lastName ? peer.lastName.charAt(0).toUpperCase() + '.' : '',
      Email: peer.email || '',
      WhatsAppNumber: peer.phoneNumber || '',
      LinkedInUrl: peer.linkedInProfileUrl || '',
      CurrentCity: peer.city || '',
      CurrentCountry: peer.country || '',
      OriginCity: peer.homeCity || '',
      OriginCountry: peer.homeCountry ?? '',
      Gender: peer.gender ?? '',
      StudyLevel: studyLevel,
    });

    // PATCH STEP 2: EDUCATION
    this.signupForm.patchValue({
      HighestLevelEducation: peer.highestLevelEducation || '',
      SchoolName: peer.highSchoolName || '',
      SchoolBoard: peer.highSchoolBoard || '',
      HighSchoolCompletionYear: peer.highSchoolCompletionYear || '',
      ClassXResult: peer.tenthGrades || '',
      ClassXIIResult: peer.twelvethGrades || '',
      CourseWork: peer.courseWork || '',

      UniversityName: peer.underGraduateCollageName || '',
      UniversityCountry: peer.underGraduateCollageCountry || '',
      UniversityCity: peer.underGraduateCollageCity || '',
      GraduationYear: peer.underGraduateOrExpectedUnderGraduationCompletionYear || '',
      DegreeSubject: peer.underGraduateMajorOrDegree || '',
      GradeAchieved: peer.underGraduateExpectedGrades ?? '',
      UndergradGpaDegreeClass: peer.underGraduateDegreeAndClass ?? '',
    });

    if (studyLevel === 'Postgraduate') {
      this.signupForm.patchValue({
        SecondUniversityName: peer.graduateCollageName || '',
        SecondUniversityCountry: peer.graduateCollageCountry || '',
        SecondUniversityCity: peer.graduateCollageCity || '',
        SecondGraduationYear: peer.graduateOrExpectedGraduationCompletionYear || '',
        SecondDegreeSubject: peer.graduateMajorOrDegree || '',
        SecondGradeAchieved: peer.graduateExpectedGrades || '',
      });
    }

    // PATCH STEP 3: TEST SCORES
    this.signupForm.patchValue({
      SATOverall: peer.satOverallScore || '',
      SATEnglish: peer.satEnglishScore || '',
      SATMath: peer.satMathScore || '',
      ACTOverall: peer.actOverallScore || '',
      ACTEnglish: peer.actEnglishScore || '',
      ACTMath: peer.actMathScore || '',
      IELTS: peer.ieltsScore || '',
      TOEFL: peer.toeflScore || '',
      APScores: peer.apScore || '',
      GREOverall: peer.greScore || '',
      GREVerbal: peer.greVerbalScore || '',
      GREQuant: peer.greQuantitativeScore || '',
      GMATOverall: peer.gmatScore || '',
      GMATSubScores: peer.gmatSubScore || '',
      WorkExpBeforeMBA: peer.workExperienceBeforeMBA ?? '',
      GraduateIELTS: peer.graduateIELTSScore ?? '',
      GraduateTOEFL: peer.graduateTOEFLScore ?? '',
    });

    // PATCH STEP 4: EXPERIENCE
    this.signupForm.patchValue({
      ShortBio: peer.shortBio ?? '',
      ScholarshipsAwards: peer.scholarshipsAndAchievements ?? '',
      Extracurriculars: peer.extraCurricularActivitiesDescription ?? peer.extraCurricularActivities ?? '',
    });

    // Populate Career Areas from API
    const rawAreas = peer.speakingCareerAreas;
    if (rawAreas != null) {
      if (Array.isArray(rawAreas) && rawAreas.length > 0) {
        this.careerAreasOptions.forEach((opt, i) => {
          const isChecked = rawAreas.some((val: any) => Number(val) === opt.value);
          this.careerAreasArray.at(i).setValue(isChecked);
        });
      } else if (typeof rawAreas === 'number' && rawAreas > 0) {
        this.careerAreasOptions.forEach((opt, i) => {
          const isChecked = (rawAreas & opt.value) !== 0;
          this.careerAreasArray.at(i).setValue(isChecked);
        });
      }
    }

    // Populate Work History from API array
    const workHistoryArray = this.workHistory;
    workHistoryArray.clear();
    if (peer.employmentHistory && Array.isArray(peer.employmentHistory) && peer.employmentHistory.length > 0) {
      peer.employmentHistory.forEach((work: any) => {
        workHistoryArray.push(this.fb.group({
          company: work.companyName || '',
          role: work.role || ''
        }));
      });
    }

    // Ensure at least 2 rows are always present
    while (workHistoryArray.length < 2) {
      workHistoryArray.push(this.createWorkHistoryGroup());
    }
    this.signupForm.markAsPristine();
  }

  onProfilePhotoSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0] ?? null;
    if (file) {
      this.selectedProfilePhotoFile = file;
      this.selectedProfilePhotoName = file.name;
      this.signupForm.patchValue({ ProfilePhoto: file });
    }
  }

  getGenderLabel(): string {
    const genderValue = this.signupForm.get('Gender')?.value;
    const match = this.genderOptions.find(option => option.value === genderValue);
    return match?.label ?? 'Not specified';
  }

  isCareerAreaChecked(index: number): boolean {
    return this.careerAreasArray.at(index).value === true;
  }

  nextStep(): void {
    this.submitted = true;
    this.markStepControlsTouched(this.currentStep);

    if (this.currentStep < this.stepTitles.length && this.isStepValid(this.currentStep)) {
      this.currentStep++;
      this.submitted = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.stepTitles.length) return;

    this.submitted = true;
    this.markStepControlsTouched(this.currentStep);

    if (step <= this.currentStep || this.isStepValid(this.currentStep)) {
      this.currentStep = step;
      this.submitted = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private markStepControlsTouched(step: number): void {
    this.getStepFields(step).forEach(name => {
      this.signupForm.get(name)?.markAsTouched();
    });
    if (step === 4) {
      this.careerAreasArray.markAllAsTouched();
      this.workHistory.markAllAsTouched();
    }
  }

  isStepValid(step: number): boolean {
    const fieldsValid = this.getStepFields(step).every(name => {
      const control = this.signupForm.get(name);
      return control ? control.valid : true;
    });

    if (step === 4) {
      return fieldsValid && this.workHistory.valid && this.selectedCareerAreas.length > 0;
    }

    return fieldsValid;
  }

  private getStepFields(step: number): string[] {
    const fields: string[] = [];
    switch (step) {
      case 1:
        fields.push(
          'FirstName', 'LastName', 'Email', 'WhatsAppNumber', 'LinkedInUrl',
          'CurrentCity', 'CurrentCountry', 'OriginCity', 'OriginCountry', 'Gender'
        );
        break;
      case 2:
        fields.push(
          'StudyLevel', 'UniversityName', 'UniversityCountry', 'UniversityCity',
          'GraduationYear', 'DegreeSubject', 'GradeAchieved',
          'SchoolName', 'SchoolBoard', 'ClassXResult', 'ClassXIIResult',
          'HighSchoolCompletionYear', 'CourseWork'
        );
        if (this.isPostgraduate) {
          fields.push(
            'SecondUniversityName', 'SecondUniversityCountry', 'SecondUniversityCity',
            'SecondGraduationYear', 'SecondDegreeSubject', 'SecondGradeAchieved'
          );
        }
        break;
      case 3:
        fields.push(
          'SATOverall', 'SATMath', 'SATEnglish', 'ACTOverall', 'ACTEnglish', 'ACTMath',
          'GRETotal', 'GREVerbal', 'GREQuant', 'GMATScore', 'GMATSubScore',
          'WorkExperience', 'IELTS', 'TOEFL', 'APScore', 'GraduateIELTS', 'GraduateTOEFL'
        );
        break;
      case 4:
        fields.push('ShortBio', 'ScholarshipsAwards', 'Extracurriculars');
        break;
      case 5:
        fields.push('AgreedToTerms');
        break;
    }
    return fields;
  }

  onSubmit(): void {
    this.submitted = true;
    this.isSubmitting = true;
    this.errorMessage = '';
    this.submitSuccess = false;

    if (this.signupForm.invalid) {
      this.errorMessage = 'Please complete all required fields and agree to the terms.';
      this.isSubmitting = false;
      return;
    }

    const values = this.signupForm.value;
    const careerAreas = this.selectedCareerAreas;

    if (this.isEditMode) {
      this.submitEdit(values, careerAreas);
    } else {
      this.submitNew(values, careerAreas);
    }
  }

  private submitNew(values: any, selectedCareerAreas: SpeakingCareerAreasEnum[]): void {
    const request: RegisterPeerReviewerRequest = {
      // STEP 1
      firstName: values.FirstName || '',
      lastName: values.LastName || '',
      email: values.Email || '',
      phoneNumber: values.WhatsAppNumber || '',
      linkedInProfileUrl: values.LinkedInUrl || '',
      city: this.normalizeText(values.CurrentCity),
      country: this.normalizeText(values.CurrentCountry),
      homeCity: this.normalizeText(values.OriginCity),
      homeCountry: this.normalizeText(values.OriginCountry),
      gender: values.Gender,
      imageUrl: this.selectedProfilePhotoFile ?? undefined,

      // STEP 2
      highestLevelEducation: this.studyLevels.find(s => s.value === values.StudyLevel)?.label || values.StudyLevel || '',
      highSchoolName: values.SchoolName || '',
      highSchoolBoard: values.SchoolBoard || '',
      highSchoolCompletionYear: values.HighSchoolCompletionYear || '',
      tenthGrades: values.ClassXResult || '',
      twelvethGrades: values.ClassXIIResult || '',
      courseWork: values.CourseWork || '',

      underGraduateCollageName: values.UniversityName || '',
      underGraduateCollageCountry: values.UniversityCountry || '',
      underGraduateCollageCity: values.UniversityCity || '',
      underGraduateOrExpectedUnderGraduationCompletionYear: values.GraduationYear || '',
      underGraduateMajorOrDegree: values.DegreeSubject || '',
      underGraduateExpectedGrades: values.GradeAchieved || '',
      underGraduateDegreeAndClass: values.UndergradGpaDegreeClass || '',

      graduateCollageName: this.isPostgraduate ? (values.SecondUniversityName || '') : '',
      graduateCollageCountry: this.isPostgraduate ? (values.SecondUniversityCountry || '') : '',
      graduateCollageCity: this.isPostgraduate ? (values.SecondUniversityCity || '') : '',
      graduateOrExpectedGraduationCompletionYear: this.isPostgraduate ? (values.SecondGraduationYear || '') : '',
      graduateMajorOrDegree: this.isPostgraduate ? (values.SecondDegreeSubject || '') : '',
      graduateExpectedGrades: this.isPostgraduate ? (values.SecondGradeAchieved || '') : '',

      // STEP 3
      satOverallScore: values.SATOverall || '',
      satEnglishScore: values.SATEnglish || '',
      satMathScore: values.SATMath || '',
      actOverallScore: values.ACTOverall || '',
      actEnglishScore: values.ACTEnglish || '',
      actMathScore: values.ACTMath || '',
      ieltsScore: values.IELTS || '',
      toeflScore: values.TOEFL || '',
      apScore: values.APScores || '',
      greScore: values.GREOverall || '',
      greVerbalScore: values.GREVerbal || '',
      greQuantitativeScore: values.GREQuant || '',
      gmatScore: values.GMATOverall || '',
      gmatSubScore: values.GMATSubScores || '',
      workExperienceBeforeMBA: values.WorkExpBeforeMBA || '',
      graduateIeltsScore: values.GraduateIELTS || '',
      graduateToeflScore: values.GraduateTOEFL || '',

      // STEP 4
      shortBio: values.ShortBio || '',
      scholarshipsAndAchievements: values.ScholarshipsAwards || '',
      extraCurricularActivitiesDescription: values.Extracurriculars || '',
      speakingCareerAreas: selectedCareerAreas,
      employmentHistory: (values.WorkHistory || []).map((item: any) => ({
        companyName: item.company || '',
        role: item.role || ''
      })),
      state: PeerReviewerState.Active
    };

    this.peerReviewService.registerPeerReviewer(request).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Your peer signup has been submitted successfully.'
        });
        this.router.navigate(['/peer-signup-thank-you']);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.handleSubmissionError(err, 'Submission failed. Please try again later.');
      }
    });
  }

  private submitEdit(values: any, selectedCareerAreas: SpeakingCareerAreasEnum[]): void {
    const request: UpdatePeerReviewerRequest = {
      applicationUserId: this.editPeerId,

      // STEP 1
      firstName: values.FirstName || '',
      lastName: values.LastName || '',
      email: values.Email || '',
      phoneNumber: values.WhatsAppNumber || '',
      linkedInProfileUrl: values.LinkedInUrl || '',
      city: this.normalizeText(values.CurrentCity),
      country: this.normalizeText(values.CurrentCountry),
      homeCity: this.normalizeText(values.OriginCity),
      homeCountry: this.normalizeText(values.OriginCountry),
      gender: values.Gender,
      imageUrl: this.selectedProfilePhotoFile ?? undefined,

      // STEP 2
      highestLevelEducation: this.studyLevels.find(s => s.value === values.StudyLevel)?.label || values.StudyLevel || '',
      highSchoolName: values.SchoolName || '',
      highSchoolBoard: values.SchoolBoard || '',
      highSchoolCompletionYear: values.HighSchoolCompletionYear || '',
      tenthGrades: values.ClassXResult || '',
      twelvethGrades: values.ClassXIIResult || '',
      courseWork: values.CourseWork || '',

      underGraduateCollageName: values.UniversityName || '',
      underGraduateCollageCountry: values.UniversityCountry || '',
      underGraduateCollageCity: values.UniversityCity || '',
      underGraduateOrExpectedUnderGraduationCompletionYear: values.GraduationYear || '',
      underGraduateMajorOrDegree: values.DegreeSubject || '',
      underGraduateExpectedGrades: values.GradeAchieved || '',
      underGraduateDegreeAndClass: values.UndergradGpaDegreeClass || '',

      graduateCollageName: this.isPostgraduate ? (values.SecondUniversityName || '') : '',
      graduateCollageCountry: this.isPostgraduate ? (values.SecondUniversityCountry || '') : '',
      graduateCollageCity: this.isPostgraduate ? (values.SecondUniversityCity || '') : '',
      graduateOrExpectedGraduationCompletionYear: this.isPostgraduate ? (values.SecondGraduationYear || '') : '',
      graduateMajorOrDegree: this.isPostgraduate ? (values.SecondDegreeSubject || '') : '',
      graduateExpectedGrades: this.isPostgraduate ? (values.SecondGradeAchieved || '') : '',

      // STEP 3
      satOverallScore: values.SATOverall || '',
      satEnglishScore: values.SATEnglish || '',
      satMathScore: values.SATMath || '',
      actOverallScore: values.ACTOverall || '',
      actEnglishScore: values.ACTEnglish || '',
      actMathScore: values.ACTMath || '',
      ieltsScore: values.IELTS || '',
      toeflScore: values.TOEFL || '',
      apScore: values.APScores || '',
      greScore: values.GREOverall || '',
      greVerbalScore: values.GREVerbal || '',
      greQuantitativeScore: values.GREQuant || '',
      gmatScore: values.GMATOverall || '',
      gmatSubScore: values.GMATSubScores || '',
      workExperienceBeforeMBA: values.WorkExpBeforeMBA || '',
      graduateIeltsScore: values.GraduateIELTS || '',
      graduateToeflScore: values.GraduateTOEFL || '',

      // STEP 4
      shortBio: values.ShortBio || '',
      scholarshipsAndAchievements: values.ScholarshipsAwards || '',
      extraCurricularActivitiesDescription: values.Extracurriculars || '',
      speakingCareerAreas: selectedCareerAreas,
      employmentHistory: (values.WorkHistory || []).map((item: any) => ({
        companyName: item.company || '',
        role: item.role || ''
      })),
      isEmailUpdate: false
    };

    this.peerReviewService.updatePeerReviewerDetails(request).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Your profile has been updated successfully.'
        });
        this.router.navigate(['/admin/peer-list']);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.handleSubmissionError(err, 'Update failed. Please try again later.');
      }
    });
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private handleSubmissionError(error: unknown, fallbackMessage: string): void {
    const detail = this.extractErrorMessage(error) || fallbackMessage;
    const isDuplicateEmail = /email.*(exist|already|duplicate|taken)|already.*email/i.test(detail);

    if (isDuplicateEmail) {
      const emailControl = this.signupForm.get('Email');
      if (emailControl) {
        emailControl.setErrors({ ...(emailControl.errors ?? {}), duplicate: true });
        emailControl.markAsTouched();
      }
      this.currentStep = 1;
      this.submitted = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    this.messageService.add({
      severity: isDuplicateEmail ? 'warn' : 'error',
      summary: isDuplicateEmail ? 'Email already exists' : 'Error',
      detail
    });
  }

  private extractErrorMessage(error: unknown): string | null {
    const err = error as {
      error?: {
        message?: string;
        errors?: Array<{ message?: string }> | Record<string, string[]>;
      };
      message?: string;
    };

    if (typeof err?.error?.message === 'string' && err.error.message.trim()) {
      return err.error.message;
    }

    const errors = err?.error?.errors;
    if (Array.isArray(errors)) {
      const firstMessage = errors.find(item => typeof item?.message === 'string' && item.message.trim())?.message;
      if (firstMessage) {
        return firstMessage;
      }
    }

    if (errors && typeof errors === 'object') {
      for (const values of Object.values(errors)) {
        if (Array.isArray(values)) {
          const first = values.find(value => typeof value === 'string' && value.trim());
          if (first) {
            return first;
          }
        }
      }
    }

    if (typeof err?.message === 'string' && err.message.trim()) {
      return err.message;
    }

    return null;
  }
}